import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Collapse from "@mui/material/Collapse"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { format } from "date-fns"
import { useState } from "react"
import { useSelector } from "react-redux"

import Column from "../../../common/components/Column"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import { formatCurrencyLong } from "../../../common/utils"
import { SUBSCRIPTION_PLANS } from "../../users/billing"
import { selectSubscription } from "../../users/reducer"
import CheckoutDrawer from "../components/CheckoutDrawer"
import { useSelectedPlanState } from "../components/SelectPlanDrawer"

export default function SubscriptionTab() {
  const [selectedPlan, setSelectedPlan] = useSelectedPlanState()
  const [checkoutDrawerOpen, setCheckoutDrawerOpen] = useState(false)
  const subscription = useSelector(selectSubscription)

  function formatDate(date: Date) {
    try {
      return format(date, "MMM dd, yyyy")
    } catch {
      return ""
    }
  }

  function daysUntilDate(date: Date) {
    return Math.max(0, Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24)))
  }

  function handleUpgradeClick() {
    setCheckoutDrawerOpen(true)
  }

  return (
    <Column sx={{ gap: 3, mt: 2 }}>
      <Collapse in={subscription.isInTrial}>
        <TrialStatusPaper elevation={0}>
          <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Row sx={{ alignItems: "center", gap: 2 }}>
              <WarningAmberIcon
                sx={{
                  color: (theme) => theme.palette.colors.error600,
                  fontSize: "32px",
                  flexShrink: 0,
                }}
              />
              <Column sx={{ gap: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: (theme) => theme.palette.colors.error700,
                  }}
                >
                  Free Plan{" "}
                  {subscription.trialFinished ? "Expired" : "Expires Soon"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: (theme) => theme.palette.colors.error700,
                    fontWeight: 600,
                  }}
                >
                  Your free plan will expire on{" "}
                  {formatDate(subscription.trialEndsOnDate)} (
                  {daysUntilDate(subscription.trialEndsOnDate)} days remaining)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.colors.error600,
                  }}
                >
                  Upgrade now to continue enjoying unlimited access to all
                  features
                </Typography>
              </Column>
            </Row>
            <TrialUpgradeButton
              variant="contained"
              onClick={handleUpgradeClick}
            >
              Upgrade Now
            </TrialUpgradeButton>
          </Row>
          <CheckoutDrawer
            open={checkoutDrawerOpen}
            onClose={() => setCheckoutDrawerOpen(false)}
            initialPlan={selectedPlan}
          />
        </TrialStatusPaper>
      </Collapse>
      <Box>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Choose a plan
        </Typography>
        <RowButColumnOnMobile
          sx={{
            gap: 2,
          }}
        >
          {SUBSCRIPTION_PLANS.map((plan) => {
            const isActive = subscription.isPlanActive(plan)
            return (
              <PlanPaper
                key={plan.id}
                elevation={0}
                onClick={() => setSelectedPlan(plan)}
                isActive={isActive}
              >
                {isActive && <ActivePlanChip label="Already on" size="small" />}
                <PlanRow>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    ${plan.price.toLocaleString()}
                  </Typography>
                </PlanRow>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.colors.gray600, mb: 1 }}
                >
                  {plan.billingCycle}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.colors.gray600 }}
                >
                  {plan.description}
                </Typography>
                {isActive && <SelectedPlanChip label="Selected" size="small" />}
              </PlanPaper>
            )
          })}
        </RowButColumnOnMobile>
      </Box>

      <Collapse in={subscription.isActive}>
        <Column sx={{ gap: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Current Subscription
            </Typography>
            <CurrentPlanPaper elevation={0}>
              <Row
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Row sx={{ alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {subscription.plan?.label}
                    </Typography>
                    <Chip
                      label={subscription.statusDisplay}
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.colors.success600,
                        color: "white",
                        fontWeight: 600,
                      }}
                      icon={
                        <CheckCircleIcon sx={{ color: "white !important" }} />
                      }
                    />
                  </Row>
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.colors.gray600 }}
                  >
                    Started on {formatDate(subscription.validUntilDate)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {formatCurrencyLong(subscription.plan?.price)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.colors.gray600 }}
                  >
                    {subscription.plan?.billingCycle}
                  </Typography>
                </Box>
              </Row>
              {subscription.isMonthly ? (
                <UpgradePlanButton
                  variant="contained"
                  onClick={handleUpgradeClick}
                >
                  Upgrade Plan
                </UpgradePlanButton>
              ) : null}
            </CurrentPlanPaper>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Subscription Details
            </Typography>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Plan Name</TableCell>
                    <TableCell>
                      <Chip label={subscription.plan?.label} size="small" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>
                      <Chip
                        label={subscription.statusDisplay}
                        size="small"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.colors.success600,
                          color: "white",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Billing Cycle</TableCell>
                    <TableCell>{subscription.plan?.billingCycle}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Price</TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrencyLong(subscription.plan?.price)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Start Date</TableCell>
                    <TableCell>{formatDate(subscription.startDate)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Next Billing</TableCell>
                    <TableCell>
                      {formatDate(subscription.validUntilDate)}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={
                      subscription.isCloseToExpiry && {
                        backgroundColor: (theme) =>
                          theme.palette.colors.error50 || "#FEF3F2",
                        "& .MuiTableCell-root": {
                          borderColor: (theme) =>
                            theme.palette.colors.error200 || "#FECDCA",
                        },
                      }
                    }
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: (theme) =>
                          subscription.isCloseToExpiry
                            ? theme.palette.colors.error700
                            : "initial",
                      }}
                    >
                      Expiry Date
                    </TableCell>
                    <TableCell>
                      <Row sx={{ alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: (theme) =>
                              subscription.isCloseToExpiry
                                ? theme.palette.colors.error600
                                : "initial",
                          }}
                        >
                          {formatDate(subscription.validUntilDate)}
                        </Typography>
                        <ValidUntilChip
                          label={`${daysUntilDate(
                            subscription.validUntilDate
                          )} days left`}
                          size="small"
                          expiryClose={subscription.isCloseToExpiry}
                        />
                      </Row>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Plan Features
            </Typography>
            <Paper elevation={0} sx={{ p: 2 }}>
              <Column sx={{ gap: 1.5 }}>
                {subscription.plan?.features.map((feature, index) => (
                  <Row key={index} sx={{ alignItems: "center", gap: 1.5 }}>
                    <CheckCircleIcon
                      sx={{
                        color: (theme) => theme.palette.colors.success600,
                        fontSize: "20px",
                      }}
                    />
                    <Typography variant="body2">{feature}</Typography>
                  </Row>
                ))}
              </Column>
            </Paper>
          </Box>
        </Column>
      </Collapse>
    </Column>
  )
}

const TrialStatusPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  border: `2px solid ${theme.palette.colors.error600}`,
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.colors.error50,
  background: (theme) =>
    `linear-gradient(135deg, ${theme.palette.colors.error50} 0%, ${theme.palette.colors.error50}80 100%)`,
}))

const TrialUpgradeButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  textTransform: "none",
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.25, 3),
  background: `linear-gradient(135deg, ${theme.palette.colors.brand500}, ${theme.palette.colors.brand700})`,
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.colors.brand600}, ${theme.palette.colors.brand700})`,
    transform: "translateY(-1px)",
  },
  transition: "all 0.3s ease",
}))

const PlanPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  flex: 1,
  border: `2px solid ${
    isActive ? theme.palette.colors.brand500 : theme.palette.colors.gray200
  }`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  backgroundColor: isActive
    ? theme.palette.colors.brand50
    : theme.palette.background.paper,
  transition: "all 0.2s ease",
  position: "relative",
}))

const ActivePlanChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: -12,
  right: 20,
  backgroundColor: theme.palette.colors.success600,
  color: "white",
  fontWeight: 700,
  fontSize: "0.75rem",
  height: "28px",
  boxShadow: `0 4px 12px ${theme.palette.colors.success600}50, 0 2px 4px ${theme.palette.colors.success600}30`,
  border: `1.5px solid ${theme.palette.colors.success700}`,
  zIndex: 1,
  "& .MuiChip-label": {
    padding: theme.spacing(0.5, 2),
  },
}))

const PlanRow = styled(Row)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
  gap: theme.spacing(1),
}))

const SelectedPlanChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  backgroundColor: theme.palette.colors.brand500,
  color: "white",
  fontWeight: 600,
}))

const CurrentPlanPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `2px solid ${theme.palette.colors.success600}`,
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.colors.success50,
}))

const UpgradePlanButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontWeight: 600,
  textTransform: "none",
  borderRadius: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${theme.palette.colors.brand500}, ${theme.palette.colors.brand700})`,
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.colors.brand600}, ${theme.palette.colors.brand700})`,
  },
}))

const ValidUntilChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "expiryClose",
})(({ theme, expiryClose }) => ({
  backgroundColor: expiryClose
    ? theme.palette.colors.error600
    : theme.palette.colors.success600,
  color: "white",
  fontWeight: 600,
  fontSize: "0.75rem",
}))
